import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";
import { MaskedLine } from "@/components/Reveal";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading");
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const data = await api.orderBySession(sessionId);
        if (data.status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="noise-overlay" data-testid="order-success-page">
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 pt-32 sm:pt-40 pb-24">
        {state === "loading" && (
          <div className="py-24 text-center" data-testid="order-loading">
            <p className="font-mono text-xs tracking-[0.4em] text-neutral-400 animate-pulse">CONFIRMING YOUR PAYMENT...</p>
          </div>
        )}

        {state === "error" && (
          <div className="border border-wire px-8 py-24 text-center" data-testid="order-error">
            <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-neutral-400">Couldn't confirm order</p>
            <p className="mt-4 font-mono text-xs tracking-[0.2em] text-neutral-500">
              IF YOUR PAYMENT WENT THROUGH, YOUR ORDER IS SAFE. CHECK YOUR EMAIL OR CONTACT US.
            </p>
            <Link to="/contact" data-testid="order-error-contact" className="mt-8 inline-block border border-wire px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-neutral-300 hover:border-white hover:text-white">
              CONTACT SUPPORT
            </Link>
          </div>
        )}

        {state === "paid" && order && (
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] text-neutral-500">PAYMENT RECEIVED</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white">
              <MaskedLine>Order Confirmed</MaskedLine>
            </h1>
            <p className="mt-6 font-script text-xl sm:text-2xl text-neutral-300" data-testid="order-thanks">
              Thank you for shopping Dejon Richh.
            </p>

            <div className="mt-12 grid grid-cols-1 gap-px border border-wire bg-wire lg:grid-cols-3">
              <div className="bg-obsidian p-8">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">ORDER NUMBER</p>
                <p className="mt-3 font-mono text-2xl tracking-[0.15em] text-white" data-testid="order-number">{order.order_number}</p>
                <p className="mt-6 font-mono text-[10px] tracking-[0.25em] text-neutral-500">CONFIRMATION SENT TO</p>
                <p className="mt-2 font-mono text-xs text-neutral-200" data-testid="order-email">{order.email || "—"}</p>
              </div>
              <div className="bg-obsidian p-8">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">SHIPPING TO</p>
                {order.shipping_address?.line1 ? (
                  <div className="mt-3 space-y-1 font-mono text-xs leading-relaxed text-neutral-200" data-testid="order-shipping-address">
                    {order.customer_name && <p>{order.customer_name}</p>}
                    <p>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</p>
                    <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</p>
                    <p>{order.shipping_address.country}</p>
                  </div>
                ) : (
                  <p className="mt-3 font-mono text-xs text-neutral-500">ADDRESS ON FILE</p>
                )}
                <p className="mt-6 font-mono text-[10px] tracking-[0.25em] text-neutral-500">ESTIMATED DELIVERY</p>
                <p className="mt-2 font-mono text-xs text-neutral-200" data-testid="order-delivery">{order.estimated_delivery}</p>
              </div>
              <div className="bg-obsidian p-8">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">TOTAL PAID</p>
                <p className="mt-3 font-script text-3xl text-white" data-testid="order-total">{money(order.total)}</p>
                <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                  {order.shipping_method || "STANDARD SHIPPING"}{order.tax ? ` — INCL. ${money(order.tax)} TAX` : ""}
                </p>
              </div>
            </div>

            <div className="mt-px border border-t-0 border-wire bg-obsidian p-8">
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">ORDER DETAILS</p>
              <div className="mt-4 divide-y divide-wire" data-testid="order-items">
                {order.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-4">
                    <div>
                      <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{item.name}</p>
                      <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">SIZE {item.size} — QTY {item.qty}</p>
                    </div>
                    <p className="font-mono text-sm text-neutral-200">{money(item.price * item.qty)}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-neutral-500">
                TRACKING INFORMATION WILL BE EMAILED TO YOU AS SOON AS YOUR ORDER SHIPS.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/track" data-testid="order-track-button"
                className="border border-wire px-8 py-4 font-mono text-[11px] tracking-[0.35em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                TRACK ORDER
              </Link>
              <Link to="/shop" data-testid="order-continue-button"
                className="group flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
                CONTINUE SHOPPING <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
